import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface ProgressAnalyticsProps {
  courseId: string;
}

const ProgressAnalytics: React.FC<ProgressAnalyticsProps> = ({ courseId }) => {
  const [dateRange, setDateRange] = useState('monthly');

  // Mock data
  const progressDistribution = [
    { range: '0-25%', count: 12 },
    { range: '26-50%', count: 28 },
    { range: '51-75%', count: 45 },
    { range: '76-99%', count: 38 },
    { range: '100%', count: 122 },
  ];

  const statusBreakdown = [
    { name: 'Passed', value: 122, color: '#82ca9d' },
    { name: 'Failed', value: 18, color: '#ff6b6b' },
    { name: 'In Progress', value: 89, color: '#ffc658' },
    { name: 'Not Started', value: 16, color: '#8884d8' },
  ];

  const progressOverTime = [
    { week: 'Week 1', progress: 15 },
    { week: 'Week 2', progress: 28 },
    { week: 'Week 3', progress: 42 },
    { week: 'Week 4', progress: 55 },
    { week: 'Week 5', progress: 68 },
  ];

  const completionTimeData = [
    { student: 'Student 1', time: 35 },
    { student: 'Student 2', time: 42 },
    { student: 'Student 3', time: 28 },
    { student: 'Student 4', time: 51 },
    { student: 'Student 5', time: 38 },
  ];

  const cohortComparison = [
    { cohort: 'Class A', progress: 72 },
    { cohort: 'Class B', progress: 68 },
    { cohort: 'Class C', progress: 65 },
    { cohort: 'Class D', progress: 71 },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Progress & Completion Analytics
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateRange}
            label="Date Range"
            onChange={(e) => setDateRange(e.target.value)}
          >
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="all">All Time</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Status Breakdown */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Student Status Breakdown
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Progress Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Progress Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={progressDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Progress Over Time */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Average Progress Over Time
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={progressOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="progress" stroke="#8884d8" strokeWidth={2} name="Progress %" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Completion Time Stats */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Completion Time Statistics
            </Typography>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Average Time
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                42 days
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Median Time
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                38 days
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Fastest
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                28 days
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Slowest
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                51 days
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Cohort Comparison */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Progress by Cohort
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={cohortComparison}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="cohort" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="progress" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top Performers */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Completion Time Sample
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell align="right">Days</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {completionTimeData.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.student}</TableCell>
                      <TableCell align="right">{row.time}</TableCell>
                      <TableCell align="right">
                        <Chip label="Completed" color="success" size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProgressAnalytics;

